/**
 * stockApi.js — DAS-164 /api/stocks/* 호출 헬퍼.
 *
 * 백엔드 (go-server/internal/handler/stock.go) 는 미국주식 적립 현황(키움증권 '주식
 * 더 모으기') 을 다룬다. 체결은 반자동(플랜 기반 pending 자동 생성 + 1탭 확정) 이며,
 * 평가(시세·환율)는 전부 자동 계산된다. API 계약: docs/specs/DAS-164/spec-impl.md §3.
 */
import apiClient from "./apiClient";

// ① Hero 요약 + 손익 분해
export const getSummary = () => apiClient.get("/api/stocks/summary");

// ⑤ 보유 종목 집계 + 현재가 병합
export const getHoldings = () => apiClient.get("/api/stocks/holdings");

// ② 적립 성과 시계열. range: "3m" | "6m" | "1y" | "all" (기본 "6m")
// accountId를 주면 그 계좌의 개별 기록만, 생략하면 전 계좌 합계(account_id=0)를 받는다 (DAS-171 P1 E-7).
export const getSeries = (range = "6m", accountId) =>
    apiClient.get("/api/stocks/series", { params: accountId ? { range, account_id: accountId } : { range } });

// ④ 적립 플랜 CRUD
export const getPlans = () => apiClient.get("/api/stocks/plans");

export const createPlan = (payload) => apiClient.post("/api/stocks/plans", payload);

export const updatePlan = (id, payload) =>
    apiClient.put(`/api/stocks/plans/${encodeURIComponent(id)}`, payload);

// active=0 소프트 비활성 (하드 삭제 아님)
export const deletePlan = (id) =>
    apiClient.delete(`/api/stocks/plans/${encodeURIComponent(id)}`);

// ⑥ 매수 기록 조회 (status 미지정 시 전체, limit 기본 50)
export const getPurchases = (params = {}) =>
    apiClient.get("/api/stocks/purchases", { params });

// 수동 매수 추가 (plan_id 없는 단발성 매수)
export const createPurchase = (payload) => apiClient.post("/api/stocks/purchases", payload);

// pending → confirmed/skipped 전환. action: "confirm" | "skip"
export const confirmPurchase = (id, payload) =>
    apiClient.put(`/api/stocks/purchases/${encodeURIComponent(id)}`, payload);

// 확정 행 통째 수정 (미래에셋 포지션 갱신 경로, DAS-169)
export const editPurchase = (id, payload) =>
    apiClient.put(`/api/stocks/purchases/${encodeURIComponent(id)}`, { ...payload, action: "edit" });

export const deletePurchase = (id) =>
    apiClient.delete(`/api/stocks/purchases/${encodeURIComponent(id)}`);

// 키움증권 REST API 자격증명. 조회는 마스킹된 형태만 반환.
// account_id가 있으면 그 계좌 기준, 없으면 broker(레거시 — 그 증권사의 sort_order 최소 계좌로 서버가 폴백) 기준이다.
// broker: "kiwoom"(해외주식 본계좌, 기본) | "kiwoom_isa"(ISA 계좌 — 국내상장 미국 ETF, DAS-170)
const credentialParams = ({ accountId, broker = "kiwoom" } = {}) =>
    accountId ? { account_id: accountId } : { broker };

export const getCredentials = (opts) =>
    apiClient.get("/api/stocks/credentials", { params: credentialParams(opts) });

export const saveCredentials = (payload, opts) =>
    apiClient.put("/api/stocks/credentials", payload, { params: credentialParams(opts) });

export const deleteCredentials = (opts) =>
    apiClient.delete("/api/stocks/credentials", { params: credentialParams(opts) });

// ⑦ 계좌 CRUD (다중 계좌, DAS-171 P1). PUT은 label/sort_order/include_in_total만 부분 갱신한다.
// DELETE는 계좌 귀속만 해제한다 — 매수 이력·확정 행은 보존된다.
export const getAccounts = () => apiClient.get("/api/stocks/accounts");

export const createAccount = (payload) => apiClient.post("/api/stocks/accounts", payload);

export const updateAccount = (id, payload) =>
    apiClient.put(`/api/stocks/accounts/${encodeURIComponent(id)}`, payload);

export const deleteAccount = (id) =>
    apiClient.delete(`/api/stocks/accounts/${encodeURIComponent(id)}`);
